import React, { useState } from "react";
import { DeleteFilled, UploadOutlined } from "@ant-design/icons";
import { Item } from "../../../types";
import WarningModal from "components/layout/components/WarningModal";

interface BatchTableProps {
  title: string;
  items: Item[];
  processingStatus: { [key: number]: string };
  progress: { [key: number]: number };
  processingItems: Item[];
  onItemClick: (itemId: string | any) => void;
  onAddClick: (id: any) => void;
  onDeleteSession: (item: Item) => void;
}

export const BatchTable: React.FC<BatchTableProps> = ({
  title,
  items,
  processingStatus,
  progress,
  processingItems,
  onItemClick,
  onAddClick,
  onDeleteSession,
}) => {
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [item, setItem] = useState<Item | null>(null);

  if (items.length === 0) return null;

  const sortedItems = [...items]
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 6);

  return (
    <div className="mt-4 border rounded-lg bg-white p-6 shadow-sm overflow-x-auto w-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <span className="text-sm text-gray-500">
          Showing latest {sortedItems.length} batch{sortedItems.length !== 1 ? "es" : ""}
        </span>
      </div>

      <table className="w-full text-sm text-left border-collapse">
        <tbody>
          {sortedItems.map((item) => {
            const isProcessing = processingItems.some((p) => p.id === item.id);
            const itemProgress = progress[item.id] ?? 0;
            const itemStatus = processingStatus[item.id];
            const profileCount =
              item.profiles_count ?? (item.files ?? item.profiles ?? []).length;

            const isCompleted =
              item.status === "Completed" || item.status === undefined;
            const progressWidth = isCompleted ? "100%" : `${itemProgress}%`;

            return (
              <React.Fragment key={item.id}>
                <tr
                  className="border-b hover:bg-gray-50 cursor-pointer"
                  onClick={() => onItemClick(item.id)}
                >
                  <td className="py-2 px-3" colSpan={6}>
                    <span className="text-gray-400">
                      <UploadOutlined className="text-2xl mb-2" />
                    </span>
                    &nbsp;&nbsp;&nbsp;
                    {item.title} | {profileCount} Profile
                    {profileCount > 1 && "s"}

                    {isProcessing && itemStatus && (
                      <span className="ml-3 text-xs text-blue-500 font-medium">
                        — {itemStatus}
                      </span>
                    )}
                  </td>

                  <td
                    className="py-2 px-3 text-right items-baseline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isProcessing && (
                      <>
                        <span className="inline-flex items-center justify-center bg-green-600 text-white rounded-full w-3 h-3">
                          <i className="ri-check-line text-[10px]"></i>
                        </span>
                        &nbsp;&nbsp;&nbsp;
                      </>
                    )}
                    <DeleteFilled
                      className="text-red-500 w-3 h-3 cursor-pointer hover:text-red-700"
                      onClick={() => {
                        setItem(item);
                        setDeleteModalVisible(true);
                      }}
                    />
                  </td>
                </tr>

                {/* Progress bar row — animated when processing, full green when done */}
                <tr>
                  <td colSpan={7}>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          isProcessing ? "bg-blue-500" : "bg-green-500"
                        }`}
                        style={{ width: progressWidth }}
                      />
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <WarningModal
        open={isDeleteModalVisible}
        title="Delete session"
        content={<>Do you want to permanently delete this session?</>}
        onConfirm={() => {
          item && onDeleteSession(item);
          setDeleteModalVisible(false);
        }}
        onCancel={() => setDeleteModalVisible(false)}
        confirmText="Yes, delete"
        cancelText="Cancel"
      />
    </div>
  );
};
